/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-negated-condition */
import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { FSBTable } from 'components/common/FSBTable';
import {
  BID_STATUS,
  activeAndInactivebidsColumns,
  defaultDeleteFilter,
  showSequenceNumber,
  NumberWithCommasFormat,
  currencyToLocal,
} from 'utils/constants';
import { isEmpty } from 'lodash';
import Loader from 'components/common/Loader';
import {
  LATEST_ALL_BIDS_QUERY,
  LATEST_ALL_BIDS_SUBSCRIPTION_QUERY,
} from 'utils/CommonGraphQL';
import { FaChevronDown } from 'react-icons/fa6';
import '../../../../dashboard-mobile.css';

/* ─── helpers ─── */
const getLatestBidSource = (latestBid) =>
  latestBid?.bidType === 'OFFER' ? latestBid?.offer : latestBid?.demand;

const getTraderName = (trader) =>
  trader?.entityName ||
  [trader?.firstName, trader?.lastName].filter(Boolean).join(' ') ||
  'N/A';

const getTraderPhone = (trader) => {
  if (!trader?.phone) return 'No phone';
  const cc = trader?.callingCode?.callingCode || trader?.callingCodeId || '';
  return `${cc}${cc ? '-' : ''}${trader.phone}`;
};

const getGradeNames = (sourceData) =>
  (sourceData?.grades || [])
    .map((g) => g?.name)
    .filter(Boolean)
    .join(', ') || 'N/A';

const getLocationText = (sourceData) => {
  const portNames = (sourceData?.portsOfDischarge || [])
    .map((p) => p?.name)
    .filter(Boolean)
    .join(', ');
  return (
    [sourceData?.city?.cityName || portNames, sourceData?.state?.stateName]
      .filter(Boolean)
      .join(', ') || 'N/A'
  );
};

const getQtyUnit = (sourceData, fallback = '') =>
  sourceData?.actualQuantityUnit || sourceData?.quantityUnit || fallback || '';

const getRateUnit = (sourceData, fallback = '') =>
  sourceData?.quantityUnit || sourceData?.actualQuantityUnit || fallback || '';

const getBidStatusLabel = (status) => {
  switch (status) {
    case 'BID_ACTIVE':
    case 'BID_IN_PROGRESS':
      return 'Active';
    case 'BID_EXPIRED':
      return 'Expired';
    case 'BID_ACCEPTED':
      return 'Accepted';
    case 'BID_REJECTED':
      return 'Rejected';
    case 'BID_WITHDRAWN':
      return 'Withdrawn';
    default:
      return status || 'N/A';
  }
};

const getBidStatusTone = (status) => {
  switch (status) {
    case 'BID_ACTIVE':
    case 'BID_IN_PROGRESS':
    case 'BID_ACCEPTED':
      return 'success';
    case 'BID_EXPIRED':
      return 'warning';
    case 'BID_REJECTED':
    case 'BID_WITHDRAWN':
      return 'danger';
    default:
      return 'muted';
  }
};

const formatBidDate = (val) => {
  if (!val) return 'N/A';
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return 'N/A';
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const formatBidTime = (val) => {
  if (!val) return '';
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-IN', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const getGroupedBidKey = (groupedBid, index) =>
  groupedBid?.id || groupedBid?.latestBid?.id || `grouped-bid-${index}`;

const BID_TABS = ['Overview', 'Details', 'History'];

/* ─── component ─── */
const CommonBids = ({ status = BID_STATUS.IN_PROGRESS }) => {
  const [filters, setFilters] = useState({
    filter: [
      { key: 'parentBid', value: '', type: 'NULL', op: 'IS_NULL' },
      { key: 'status', value: status, type: 'STRING', op: 'EQ' },
      { ...defaultDeleteFilter },
    ],
    sortCol: 'latestBid.id',
    sortDirection: 'desc',
    pageNo: 1,
    pageSize: 10,
  });

  const [expandedCardId, setExpandedCardId] = useState(null);
  const [activeTabs, setActiveTabs] = useState({});

  const { data, error, loading, subscribeToMore } = useQuery(
    LATEST_ALL_BIDS_QUERY,
    {
      variables: { ...filters },
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    },
  );

  const findIndexOfItem = (item) =>
    activeAndInactivebidsColumns.findIndex((itm) => itm.accessorKey === item);

  activeAndInactivebidsColumns[findIndexOfItem('SNo')].cell = (cell) =>
    showSequenceNumber(cell, filters);

  useEffect(() => {
    const deletedObj = filters.filter.filter((val) => val.key !== 'isDeleted');
    const unsubscribe = subscribeToMore({
      document: LATEST_ALL_BIDS_SUBSCRIPTION_QUERY,
      variables: { ...filters, filter: deletedObj },
      updateQuery: (prev, { subscriptionData }) => {
        if (!prev || isEmpty(prev)) return '';
        if (!subscriptionData.data) return prev;
        return subscriptionData.data;
      },
    });
    return () => unsubscribe();
  }, []);

  if (loading && !data?.allBids?.groupedBids) return <Loader />;
  if (error && !data?.allBids?.groupedBids)
    return `There is an error on this page : ${error.message} `;
  if (!data?.allBids?.groupedBids) return 'Active Bids not available';

  const { totalCount, totalPages } = data.allBids;

  const handlePageChange = ({ pageSize, pageNo }) => {
    setFilters({
      ...filters,
      pageNo: totalCount < pageSize ? 1 : pageNo,
      pageSize,
    });
  };

  const onSortChange = (sort) => {
    setFilters({
      ...filters,
      sortCol: sort()[0].id,
      sortDirection: sort()[0].desc ? 'DESC' : 'ASC',
    });
  };

  const sortBy = [
    filters.sortCol === null
      ? { id: null, desc: null }
      : { id: filters.sortCol, desc: filters.sortDirection === 'DESC' },
  ];

  const groupedBids = data?.allBids?.groupedBids || [];

  const renderHistory = (groupedBid) => {
    const bids = groupedBid?.allBids || [];
    const latestBid = groupedBid?.latestBid || {};
    const sourceData = getLatestBidSource(latestBid);
    const qtyUnit = getQtyUnit(sourceData, latestBid?.quantityUnit);
    const rateUnit = getRateUnit(sourceData, latestBid?.quantityUnit);

    if (!bids.length) {
      return (
        <div className="dashboard-mobile-history__empty">No bids available</div>
      );
    }

    return (
      <div className="dashboard-mobile-history">
        <div className="dashboard-mobile-history__title">
          <span>All Bids</span>
          <span className="dashboard-mobile-card__pill muted">
            {bids.length}
          </span>
        </div>
        <div className="dashboard-mobile-history__list">
          {bids.map((bid) => {
            const isSeller = bid?.bidderRole === 'SELLER';
            const bidQtyUnit = getQtyUnit(
              getLatestBidSource(bid),
              bid?.quantityUnit,
            );
            const bidRateUnit = getRateUnit(
              getLatestBidSource(bid),
              bid?.quantityUnit,
            );
            return (
              <div
                className={`dashboard-mobile-history__item ${isSeller ? 'seller' : 'buyer'}`}
                key={bid?.id || bid?.createdAt}
              >
                <div className="dashboard-mobile-history__meta">
                  <span>{isSeller ? 'Seller' : 'Buyer'}</span>
                  <strong>{getTraderName(bid?.bidder)}</strong>
                </div>
                <div className="dashboard-mobile-history__quote">
                  <span>
                    {NumberWithCommasFormat(bid?.quantity)} {bidQtyUnit}
                  </span>
                  <span>
                    {currencyToLocal(
                      bid?.price,
                      bid?.rateUnit?.isoCurrencyCode,
                    )}
                    /{bidRateUnit}
                  </span>
                </div>
                <div className="dashboard-mobile-history__time">
                  {formatBidTime(bid?.createdAt)}
                </div>
              </div>
            );
          })}
          <div
            className={`dashboard-mobile-card__pill ${getBidStatusTone(latestBid?.status)}`}
            style={{ marginTop: 8 }}
          >
            Last Bid:{' '}
            {currencyToLocal(
              latestBid?.price,
              latestBid?.rateUnit?.isoCurrencyCode,
            )}{' '}
            | Expires: {formatBidDate(latestBid?.bidExpireTime)}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* ── Mobile Card List ── */}
      <div className="dashboard-mobile-list">
        {groupedBids.map((groupedBid, index) => {
          const cardId = getGroupedBidKey(groupedBid, index);
          const isExpanded = expandedCardId === cardId;
          const activeTab = activeTabs[cardId] || BID_TABS[0];
          const latestBid = groupedBid?.latestBid || {};
          const sourceData = getLatestBidSource(latestBid);
          const seller = sourceData?.trader;
          const qtyUnit = getQtyUnit(sourceData, latestBid?.quantityUnit);
          const rateUnit = getRateUnit(sourceData, latestBid?.quantityUnit);
          const seqNo =
            ((filters.pageNo || 1) - 1) * (filters.pageSize || 10) + index + 1;

          const overviewRows = [
            { label: 'S.No', value: seqNo },
            { label: 'Seller', value: getTraderName(seller) },
            { label: 'Seller Phone', value: getTraderPhone(seller) },
            { label: 'Grade', value: getGradeNames(sourceData), tone: 'badge' },
            {
              label: 'Status',
              value: getBidStatusLabel(latestBid?.status),
              tone: 'status',
              status: latestBid?.status,
            },
            {
              label: 'Type',
              value: latestBid?.bidType || 'N/A',
              tone: 'badge',
            },
          ];

          const detailRows = [
            { label: 'Last Bidder', value: getTraderName(latestBid?.bidder) },
            { label: 'Bidder Phone', value: getTraderPhone(latestBid?.bidder) },
            { label: 'Location', value: getLocationText(sourceData) },
            {
              label: 'Total Qty',
              value: `${NumberWithCommasFormat(sourceData?.totalQty)} ${qtyUnit}`,
            },
            {
              label: 'Min. Qty',
              value: `${NumberWithCommasFormat(sourceData?.minimumQty)} ${qtyUnit}`,
            },
            {
              label: 'Rate',
              value: `${currencyToLocal(sourceData?.rate, latestBid?.rateUnit?.isoCurrencyCode)}/${rateUnit}`,
            },
            {
              label: 'Bid Rate',
              value: `${currencyToLocal(latestBid?.price, latestBid?.rateUnit?.isoCurrencyCode)}/${rateUnit}`,
            },
            {
              label: 'Bid Qty',
              value: `${NumberWithCommasFormat(latestBid?.quantity)} ${qtyUnit}`,
            },
            {
              label: 'Bid Expire',
              value: formatBidDate(latestBid?.bidExpireTime),
            },
          ];

          const activeSection =
            activeTab === 'Overview' ? overviewRows : detailRows;

          return (
            <div className="dashboard-mobile-card" key={cardId}>
              <div className="dashboard-mobile-card__summary">
                <div className="dashboard-mobile-card__identity">
                  <span className="dashboard-mobile-card__name">
                    {getTraderName(seller)}
                  </span>
                  <div className="dashboard-mobile-card__meta">
                    {getGradeNames(sourceData)} | {latestBid?.bidType || 'N/A'}
                  </div>
                </div>
                <div className="dashboard-mobile-card__aside">
                  <span
                    className={`dashboard-mobile-card__pill ${getBidStatusTone(latestBid?.status)}`}
                  >
                    {getBidStatusLabel(latestBid?.status)}
                  </span>
                  <div className="dashboard-mobile-card__tools">
                    <button
                      type="button"
                      className={`dashboard-mobile-card__expand-toggle ${isExpanded ? 'is-open' : ''}`}
                      onClick={() =>
                        setExpandedCardId((prev) =>
                          prev === cardId ? null : cardId,
                        )
                      }
                      aria-expanded={isExpanded}
                      aria-label={isExpanded ? 'Collapse' : 'Expand'}
                    >
                      <FaChevronDown />
                    </button>
                  </div>
                </div>
              </div>

              {isExpanded && (
                <div className="dashboard-mobile-card__body">
                  <div className="dashboard-mobile-card__tabs">
                    {BID_TABS.map((tab) => (
                      <button
                        key={tab}
                        type="button"
                        className={`dashboard-mobile-card__tab ${activeTab === tab ? 'active' : ''}`}
                        onClick={() =>
                          setActiveTabs((prev) => ({ ...prev, [cardId]: tab }))
                        }
                      >
                        {tab}
                      </button>
                    ))}
                  </div>

                  {activeTab === 'History' ? (
                    renderHistory(groupedBid)
                  ) : (
                    <div className="dashboard-mobile-card__detail-list">
                      {activeSection.map((item) => (
                        <div
                          className="dashboard-mobile-card__detail-row"
                          key={item.label}
                        >
                          <div className="dashboard-mobile-card__detail-label">
                            {item.label}
                          </div>
                          <div className="dashboard-mobile-card__detail-value">
                            {item.tone === 'badge' ? (
                              <span className="dashboard-mobile-card__pill muted">
                                {item.value}
                              </span>
                            ) : item.tone === 'status' ? (
                              <span
                                className={`dashboard-mobile-card__pill ${getBidStatusTone(item.status)}`}
                              >
                                {item.value}
                              </span>
                            ) : (
                              item.value
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Mobile Pagination ── */}
      {totalPages ? (
        <div className="dashboard-mobile-pagination">
          <span className="dashboard-mobile-pagination__count">
            {filters.pageNo} of {totalPages}
          </span>
          <select
            className="page-select form-select table-select f-12 fw-normal dashboard-mobile-pagination__select"
            value={filters.pageSize}
            onChange={(e) =>
              handlePageChange({ pageNo: 1, pageSize: Number(e.target.value) })
            }
          >
            {[10, 20, 30, 40, 50].map((size) => (
              <option key={size} value={size}>
                {size} per page
              </option>
            ))}
          </select>
          <div className="dashboard-mobile-pagination__actions">
            <button
              type="button"
              onClick={() =>
                handlePageChange({ pageNo: 1, pageSize: filters.pageSize })
              }
              disabled={filters.pageNo <= 1}
            >
              {'<<'}
            </button>
            <button
              type="button"
              onClick={() =>
                handlePageChange({
                  pageNo: filters.pageNo - 1,
                  pageSize: filters.pageSize,
                })
              }
              disabled={filters.pageNo <= 1}
            >
              {'<'}
            </button>
            <button
              type="button"
              onClick={() =>
                handlePageChange({
                  pageNo: filters.pageNo + 1,
                  pageSize: filters.pageSize,
                })
              }
              disabled={filters.pageNo >= totalPages}
            >
              {'>'}
            </button>
            <button
              type="button"
              onClick={() =>
                handlePageChange({
                  pageNo: totalPages,
                  pageSize: filters.pageSize,
                })
              }
              disabled={filters.pageNo >= totalPages}
            >
              {'>>'}
            </button>
          </div>
        </div>
      ) : null}

      {/* ── Desktop Table (hidden on mobile) ── */}
      <div className="dashboard-mobile-hide-table">
        <FSBTable
          data={data?.allBids?.groupedBids || []}
          columns={activeAndInactivebidsColumns}
          totalCount={totalCount || 0}
          totalPages={totalPages || 0}
          currentPageNo={filters.pageNo}
          pageSize={filters.pageSize}
          keyField="id"
          showSearch={false}
          showFilters={false}
          showDelete={false}
          showPagination
          remote
          striped={false}
          hover={false}
          bordered={false}
          wrapperClass="table-responsive word-break"
          handlePageChange={handlePageChange}
          onSortChange={onSortChange}
          sorting={sortBy}
          searchPlaceholder="Search Vendors"
        />
      </div>
    </>
  );
};

export default CommonBids;
